import { X } from "lucide-react";
import type { ComponentProps } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

/* The glass language's four gradients live in index.css as custom properties (`--pill-fill`,
 * `--pill-rim`, `--track-fill`, `--track-rim`) so the tabs, the count pill, the close button and
 * the shared Button draw from one definition. Both fills fade to translucent, so here the
 * gradient rims are masked overlay layers (`GradientRim`) — a border-box background would show
 * through the fill and flatten the glass. (The Button paints an opaque base under the fill
 * instead, so it can carry its rim as a plain two-layer background.) */
export const PILL_FILL = "var(--pill-fill)";
export const PILL_RIM = "var(--pill-rim)";
export const TRACK_FILL = "var(--track-fill)";
export const TRACK_RIM = "var(--track-rim)";
/** The badge-like chips' glass (time left): the track's, lit from the top centre instead of the
 *  diagonal — the same light as the Badge component and the cards. */
export const CHIP_FILL = "var(--chip-fill)";
export const CHIP_RIM = "var(--chip-rim)";

/** A 1px gradient border: the gradient painted edge-to-edge, masked down to the rim. */
export const GradientRim = ({ background }: { background: string }) => (
  <span
    aria-hidden
    className="pointer-events-none absolute inset-0 rounded-[inherit] p-px"
    style={{
      background,
      mask: "linear-gradient(#000 0 0) content-box exclude, linear-gradient(#000 0 0)",
    }}
  />
);

/**
 * Close control sized and surfaced like the segmented tab track (same fill, rim and height), so
 * tabs + close read as one row of chrome in the in-game panel on phones — the standalone top
 * row it replaced was mostly dead space.
 */
export const TabRowCloseButton = ({
  className,
  ...props
}: ComponentProps<"button">) => {
  const { t } = useTranslation();
  return (
    // Rest props spread last so a Radix `asChild` parent (DialogClose) can inject its handler.
    <button
      type="button"
      aria-label={t("general.close")}
      {...props}
      className={cn(
        "focus-ring relative flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-full text-muted-foreground transition-colors outline-none hover:text-foreground active:text-foreground lg:size-[46px]",
        className
      )}
      // The glass fill over an opaque card base: the close floats over the cover art and the
      // game, and without a backdrop blur the translucent fill alone let both show through.
      style={{ background: `${TRACK_FILL}, var(--track-base)` }}
    >
      <GradientRim background={TRACK_RIM} />
      <X className="relative size-5" aria-hidden="true" />
    </button>
  );
};
